import { combineReducers } from 'redux';
import axios from 'axios';
import humps from 'humps';
import { getFormValues, getFormSyncErrors, change as formChange } from 'redux-form';
import { createAction } from 'redux-actions';
import _ from 'lodash';

import * as Urls from 'src/constants/EndpointUrls';
import { concatParamsToUrl } from 'src/utils/Http';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import { editItemFailure, getItemErrors } from 'src/reducers/common/itemReducer';
import {
  fetchList,
  fetchListSuccess,
  fetchListFailure,
  updateOne,
  setExtras,
  getExtras,
  setList,
  getList,
  getListPageCount
} from 'src/reducers/common/listReducer';
import { setGlobalSuccesses, setGlobalErrors } from 'src/reducers/global';

export const REDUCER_NAME = 'monthlyRevisions';

export const UI_SET = 'REVISIONS_UI/SET';
export const UI_CHANGE_SHOW_MODE = 'REVISIONS_UI/CHANGE_SHOW_MODE';
export const UI_CHANGE_CALC_MODE = 'REVISIONS_UI/UI_CHANGE_CALC_MODE';
export const UI_NOTIFY_VALUE_CHANGES = 'REVISIONS_UI/UI_NOTIFY_VALUE_CHANGES';
export const UI_RESET_VALUE_CHANGES = 'REVISIONS_UI/UI_RESET_VALUE_CHANGES';

export const uiSet = createAction(UI_SET);
export const uiChangeShowMode = createAction(UI_CHANGE_SHOW_MODE);
export const uiChangeCalcMode = createAction(UI_CHANGE_CALC_MODE);
export const uiNotifyValueChanges = createAction(UI_NOTIFY_VALUE_CHANGES);
export const uiResetValueChanges = createAction(UI_RESET_VALUE_CHANGES);

export const getUiShowMode = (state, revision) => _.get(state, `monthlyRevisions.ui.revisions.${revision.id}.showMode`);
export const INIT_CALC_MODE = {
  modifiedAverageAmount: 'AUTO',
  changedAmount: 'AUTO',
  retroactiveAmount: 'AUTO'
};
export const getUiCalcMode = (state, revision, name) =>
  _.get(state, `monthlyRevisions.ui.revisions.${revision.id}.calcMode.${name}`);
export const getReadyForRecalculating = (state, revision) =>
  _.get(state, `monthlyRevisions.ui.revisions.${revision.id}.readyForRecalculating`);
export const getChangedYearMonths = state => _.get(state, 'monthlyRevisions.ui.changedYearMonths');
export const getLastRevisionYearMonths = state => _.get(getExtras(REDUCER_NAME, state), 'lastRevisionYearMonths');
export const getTargetRevisions = state => _.get(getExtras(REDUCER_NAME, state), 'targetRevisions');
export const getStaledRevisions = state => _.get(getExtras(REDUCER_NAME, state), 'staledRevisions');

export const transformRevisionsToFormValues = (reducerName, state) =>
  state[reducerName].list.data.reduce(
    (pre, revision) => ({ revisions: { ...pre.revisions, [revision.id]: revision } }),
    { revisions: {} }
  );

export const getConfirmRevisionErrors = state => {
  const errors = getItemErrors(REDUCER_NAME, state) || [{}];
  return (errors[0] || {}).changeMonthlyRevisionErrors || [];
};

// Async Action Creators
export const fetchRevisions = queries => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));

    const response = await axios.get(Urls.MONTHLY_REVISION_SEARCH, { params: humps.decamelizeKeys(queries) });
    const payload = {
      data: response.data.payload.revisions,
      pageCount: response.data.payload.totalPages,
      totalCount: response.data.payload.totalCount
    };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    // 標準報酬月額改訂対象者情報
    const targetParams = Object.assign(response.data.payload.targetDates, {
      targetCount: response.data.payload.targetRevisions.length,
      lastRevisionYearMonths: response.data.payload.lastRevisionYearMonths,
      targetRevisions: response.data.payload.targetRevisions
    });
    dispatch(setExtras(REDUCER_NAME, targetParams));

    // 従前反映対象者情報
    dispatch(setExtras(REDUCER_NAME, { staledRevisions: response.data.payload.staledRevisions }));

    dispatch(
      uiSet({ revisions: response.data.payload.revisions, changedYearMonths: response.data.payload.changedYearMonths })
    );
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const editRevision = revisionId => async (dispatch, getState) => {
  try {
    const state = getState();
    const revision = _.get(getFormValues('revisions')(state), `revisions.${revisionId}`);

    // エラーがあるならスキップ
    if (_.get(getFormSyncErrors('revisions')(state), `revisions.${revisionId}`)) {
      return;
    }

    const httpBody = {
      revision
    };

    const updatePayUrl = concatParamsToUrl(Urls.MONTHLY_REVISION_UPDATE, { id: revision.id });
    const response = await axios.put(updatePayUrl, httpBody);

    dispatch(formChange('revisions', `revisions.${revisionId}`, response.data.payload.revision));
    dispatch(updateOne(REDUCER_NAME, { id: revisionId, item: response.data.payload.revision }));
    dispatch(uiChangeShowMode({ revision: response.data.payload.revision, showMode: 'SHOW' }));

    _.forIn(INIT_CALC_MODE, (value, key) => {
      dispatch(uiChangeCalcMode({ revision, name: key, calcMode: 'AUTO' }));
    });
    dispatch(uiResetValueChanges({ revision }));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const recalculateRevision = revisionId => async (dispatch, getState) => {
  try {
    const state = getState();
    const revision = _.get(getFormValues('revisions')(state), `revisions.${revisionId}`);

    // エラーがあるならスキップ
    if (_.get(getFormSyncErrors('revisions')(state), `revisions.${revisionId}`)) {
      return;
    }

    if (!getReadyForRecalculating(state, revision)) {
      return;
    }

    // CalcModeに登録されている対象のキーでstateがAUTOの場合、サーバー側で自動算出した値をセットするためキー自体を送信しない
    _.forIn(revision, (value, name) => {
      if (getUiCalcMode(state, revision, name) === 'AUTO') {
        delete revision[name];
      }
    });

    const httpBody = {
      revision
    };

    const updatePayUrl = concatParamsToUrl(Urls.MONTHLY_REVISION_RECALCULATE, { id: revision.id });
    const response = await axios.post(updatePayUrl, httpBody);

    dispatch(formChange('revisions', `revisions.${revisionId}`, response.data.payload.revision));

    dispatch(uiResetValueChanges({ revision }));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const resetRevision = revisionId => async (dispatch, getState) => {
  try {
    const state = getState();
    const revision = _.get(getFormValues('revisions')(state), `revisions.${revisionId}`);

    const updatePayUrl = concatParamsToUrl(Urls.MONTHLY_REVISION_RESET, { id: revisionId });
    const response = await axios.post(updatePayUrl);

    dispatch(formChange('revisions', `revisions.${revisionId}`, response.data.payload.revision));

    _.forIn(INIT_CALC_MODE, (value, name) => {
      dispatch(uiChangeCalcMode({ revision, name, calcMode: 'AUTO' }));
    });
    dispatch(uiResetValueChanges({ revision }));
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

export const fixBeforeMonthlyInsuranceAmounts = revisionIds => async (dispatch, getState) => {
  try {
    const requestUrl = Urls.MONTHLY_REVISION_FIX_BEFORE_MONTHLY_INSURANCE_AMOUNTS;
    const response = await axios.put(requestUrl, { revisionIds });
    if (response.status === 200) {
      const fixedRevisions = response.data.fixedRevisions;
      let monthlyRevisions = getList(REDUCER_NAME, getState());
      let targetRevisions = getTargetRevisions(getState());
      let staledRevisions = getStaledRevisions(getState());
      fixedRevisions.forEach(fixedRevision => {
        // 従前反映を行なうと標準報酬月額改訂対象（targetRevisions）かどうかが変化する可能性があるので、
        // 対象外であれば削除、対象であれば追加する
        targetRevisions = _.reject(targetRevisions, { id: fixedRevision.id, isSubject: false });
        if (fixedRevision.isSubject && _.filter(targetRevisions, { id: fixedRevision.id }).length === 0) {
          targetRevisions.push(fixedRevision);
        }

        staledRevisions = _.reject(staledRevisions, ['id', fixedRevision.id]);
        monthlyRevisions = _.map(monthlyRevisions, item => {
          const newItem = item;
          if (item.id === fixedRevision.id) {
            newItem.beforeMonthlyCompensationHealthInsuranceAmount =
              fixedRevision.beforeMonthlyCompensationHealthInsuranceAmount;
            newItem.beforeMonthlyRemunerationPensionInsuranceAmount =
              fixedRevision.beforeMonthlyRemunerationPensionInsuranceAmount;
            newItem.isSubject = fixedRevision.isSubject;
            newItem.isStaleBeforeMonthlyInsuranceAmounts = fixedRevision.isStaleBeforeMonthlyInsuranceAmounts;
          }
          return newItem;
        });
      });
      dispatch(setExtras(REDUCER_NAME, { staledRevisions }));
      dispatch(
        setList(REDUCER_NAME, { data: monthlyRevisions, pageCount: getListPageCount(REDUCER_NAME, getState()) })
      );
      dispatch(formChange('revisions', monthlyRevisions));
      dispatch(setGlobalSuccesses('標準報酬月額を反映しました'));
    } else {
      dispatch(setGlobalErrors('エラーが発生しました。時間をおいて再度お試しください'));
    }
  } catch (exception) {
    dispatch(setGlobalErrors('エラーが発生しました。時間をおいて再度お試しください'));
  }
};

export const changeMonthlyRevisionConfirm = (props, callBack) => async dispatch => {
  const revisionIds = [];
  props.revisions.forEach(revision => {
    if (revision.isSubject) revisionIds.push(revision.id);
  });

  try {
    const httpBody = humps.decamelizeKeys({ revisionIds, revisionYearMonth: props.yearMonth });
    const response = await axios.put(Urls.MONTHLY_REVISION_CONFIRM, httpBody);
    if (response.status === 200) {
      dispatch(setGlobalSuccesses('標準報酬月額を改定しました'));
    } else {
      dispatch(setGlobalErrors('標準報酬月額の改定処理が失敗しました'));
    }
    callBack();
  } catch (exception) {
    dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
  }
};

const uiReducer = (state = { revisions: {}, changedYearMonths: [] }, action) => {
  switch (action.type) {
    case UI_SET:
      return {
        changedYearMonths: action.payload.changedYearMonths,
        revisions: action.payload.revisions.reduce(
          (pre, revision) => ({
            ...pre,
            [revision.id]: {
              showMode: 'SHOW',
              readyForRecalculating: false,
              calcMode: { ...INIT_CALC_MODE }
            }
          }),
          {}
        )
      };
    case UI_CHANGE_SHOW_MODE:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: {
            ...state.revisions[action.payload.revision.id],
            showMode: action.payload.showMode
          }
        }
      };
    case UI_CHANGE_CALC_MODE:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: {
            ...state.revisions[action.payload.revision.id],
            calcMode: {
              ...state.revisions[action.payload.revision.id].calcMode,
              [action.payload.name]: action.payload.calcMode
            }
          }
        }
      };
    case UI_NOTIFY_VALUE_CHANGES:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: { ...state.revisions[action.payload.revision.id], readyForRecalculating: true }
        }
      };
    case UI_RESET_VALUE_CHANGES:
      return {
        ...state,
        revisions: {
          ...state.revisions,
          [action.payload.revision.id]: { ...state.revisions[action.payload.revision.id], readyForRecalculating: false }
        }
      };
    default:
      return state;
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME),
  ui: uiReducer
});
